import { faChevronRight as fasChevronRight } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type { PureMeasureComponentProps } from "@component/PureMeasureComponent";
import { PureMeasureComponent } from "@component/PureMeasureComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import imgLogoColor from "@img/logos/bankmonitor.svg";
import imgLogoWhite from "@img/logos/bankmonitor-white.svg";
import { ButtonLegacy, findErrorInput, getUrlParameter, isFocusableInput, PromisePool } from "@shared/ui";
import { GoogleAnalytics4 } from "@shared/lib/google-analytics-4";
import type { GA4EventParameters } from "@shared/lib/google-analytics-4.type";
import { logError } from "@shared/lib/sentry-error";
import autobind from "autobind-decorator";
import type { FormApi, FormState } from "final-form";
import createDecorator from "final-form-focus";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import React from "react";
import { Form } from "react-bootstrap";
import { Form as FinalForm, FormSpy } from "react-final-form";
import type { LeadFormStepContainerContextType } from "./LeadFormStepContainerContext";
import { LeadFormStepContainerContext } from "./LeadFormStepContainerContext";

type HtmlInput = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
const defColorScheme = getUrlParameter("colorScheme", "dark") === "light" ? "light" : "dark";

export interface AbstractStepProps extends PureMeasureComponentProps {
	className?: string;

	stepName: string;
	defaultTarget?: string;

	canStepBack?: boolean;
	bottomLogo?: boolean;

	colorVersion?: "black" | "white";
}

export type AbstractStepState = {};

export abstract class AbstractStep<
	FormFields extends Record<string, any> = {},
	Props extends AbstractStepProps = AbstractStepProps,
	State extends AbstractStepState = AbstractStepState,
> extends PureMeasureComponent<Props, State> {
	public static contextType = LeadFormStepContainerContext;
	public readonly context: React.ContextType<typeof LeadFormStepContainerContext>;
	public static defaultProps = {
		canStepBack: false,
		bottomLogo: false,
	};
	private target: string = undefined;
	private form: FormApi<FormFields> = undefined;
	private readonly formRef: React.RefObject<HTMLFormElement> = React.createRef();
	private readonly focusOnError: any;
	protected readonly promisePool = new PromisePool();

	public constructor(props: Props) {
		super(props);
		this.focusOnError = createDecorator(this.getFormInputs, findErrorInput);
		this.state = this.getInitialState();
	}

	protected getInitialState(): State {
		return {} as unknown as State;
	}

	protected initValues(): Partial<FormFields> {
		return {};
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	protected isEqual(a: FormFields, b: FormFields): boolean {
		return JSON.stringify(a) === JSON.stringify(b);
	}

	protected getStepProps(): LeadFormStepContainerContextType & {
		stepName: string;
		canStepBack: boolean;
	} {
		const { activeStep, bottomLogo: globalBottomLogo, colorScheme = defColorScheme, ...context } = this.context;
		const { stepName, bottomLogo, canStepBack } = this.props;

		return {
			...context,
			colorScheme,
			stepName,
			activeStep: activeStep || stepName,
			bottomLogo: bottomLogo || globalBottomLogo,
			canStepBack,
		};
	}

	protected isDisabled(): boolean {
		return false;
	}

	protected getCommonEventParameters(): Partial<GA4EventParameters> {
		const { ga4Event: contextGa4Event } = this.context;
		const { ga4Event: propsGa4Event } = this.props;

		return {
			...contextGa4Event,
			...super.getCommonEventParameters(),
			instanceId:
				propsGa4Event?.instanceId ||
				contextGa4Event?.instanceId ||
				super.getCommonEventParameters()?.instanceId,
			componentName: [contextGa4Event.componentName || propsGa4Event.componentName, this.getComponentName()]
				.filter(Boolean)
				.join("/"),
		};
	}

	public render(): ReactNode {
		const { stepName, activeStep, activeAnim, stepNumber } = this.getStepProps();
		const formClassName = activeStep === stepName ? compact([this.getClassName(), activeAnim]).join(" ") : "d-none";

		if (activeStep !== stepName) {
			return null;
		}

		return (
			<FinalForm<FormFields>
				initialValuesEqual={this.isEqual}
				initialValues={this.initValues()}
				decorators={[this.focusOnError]}
				onSubmit={(vals) => this.onSubmit(vals)}
			>
				{({ handleSubmit, values, form }) => {
					this.form = form;

					return (
						<>
							<Form
								ref={this.formRef}
								className={formClassName}
								onSubmit={(element) => {
									const { hasValidationErrors, errors } = form.getState();

									if (hasValidationErrors) {
										this.sendEvent("BM_stepInvalid", {
											buttonId: GoogleAnalytics4.generateId("bi"),
											stepName,
											stepNumber,
											...errors,
										});
									}

									return handleSubmit(element);
								}}
								noValidate
							>
								<FormSpy
									subscription={{ touched: true }}
									onChange={(props: FormState<FormFields>) => {
										if (Object.values(props.touched).includes(true)) {
											this.onChange(props.values);
										}
									}}
								/>
								{this.renderContent(values, form)}
							</Form>
							{this.renderFooter()}
						</>
					);
				}}
			</FinalForm>
		);
	}

	protected abstract renderContent(values: FormFields, form: FormApi<FormFields>): ReactNode;

	protected renderFooter(): ReactNode {
		const { activeStep, bottomLogo, colorScheme, stepName, previousSteps, canStepBack } = this.getStepProps();
		const prevStep = previousSteps.length ? previousSteps[previousSteps.length - 1] : undefined;

		if ((canStepBack || bottomLogo) && activeStep === stepName) {
			return (
				<div className="d-flex align-items-center justify-content-between p-2">
					{canStepBack && prevStep && (
						<ButtonLegacy
							variant={colorScheme === "dark" ? "light" : "dark"}
							outlined
							size="sm"
							data-testid="button-step-back"
							icon={<FontAwesomeIcon icon={fasChevronRight} rotation={180} />}
							onClick={() => this.onStep(prevStep, false)}
							disabled={this.isDisabled()}
						>
							Vissza
						</ButtonLegacy>
					)}
					{bottomLogo && (
						<a href="/" className="ml-auto" target="_blank" rel="noreferrer">
							<img
								width={176}
								height={32}
								src={colorScheme === "light" ? imgLogoColor : imgLogoWhite}
								alt="Bankmonitor"
								loading="eager"
							/>
						</a>
					)}
				</div>
			);
		}

		return null;
	}

	@autobind
	protected onSubmit(data: FormFields, _realSubmit = true): void {
		const { onStep } = this.context;
		const { defaultTarget } = this.props;

		if (onStep) {
			onStep(this.target || defaultTarget, data);
		}

		this.target = undefined;
	}

	@autobind
	protected onChange(data: FormFields): void {
		const { onChange } = this.context;
		const { stepName } = this.props;

		if (onChange) {
			onChange(stepName, data);
		}
	}

	@autobind
	protected onStep(target: string, realSubmit = true): void {
		this.target = target;

		if (realSubmit) {
			this.form.submit()?.catch((error) => logError(error));
		} else {
			this.onSubmit(this.form.getState().values, false);
		}
	}

	protected getClassName(): string {
		const { className, colorVersion } = this.props;
		const isDarkVersion = colorVersion === "black";

		return compact([`card small-calculator-step ${isDarkVersion ? "bg-black text-white" : ""}`, className]).join(
			" ",
		);
	}

	@autobind
	private getFormInputs(): HtmlInput[] {
		const form = this.formRef?.current;

		if (typeof document === "undefined" || !form) {
			return [];
		}

		return Array.prototype.slice.call(form).filter(isFocusableInput);
	}
}
